import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useFragment } from "react-relay";

import { useFacilityCourseNoun } from "@/features/Facility/hooks/useFacilityCourseNoun";
import {
  ClockIcon,
  LocationIcon,
  PeopleFillIcon,
  FlagIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { formatHHMM } from "@whoosh/utilities/src/dates";

import { getBookingPlayerNoun } from "../../../../utils/getBookingPlayerNoun";
import { useShouldPresentBookingAsLockedRequest } from "../AvailableTeeTimes/hooks/useShouldPresentBookingAsLockedRequest";
import { DetailRow } from "./DetailRow";
import { RequestDetails_booking$key } from "./__generated__/RequestDetails_booking.graphql";
import { getHoleCountString } from "./util/getHoleCountString";

type Props = {
  bookingRef: RequestDetails_booking$key | null;
};

export const RequestDetails = ({ bookingRef }: Props) => {
  const data = useFragment(RequestDetailsFragment, bookingRef);
  const isLockedRequest = useShouldPresentBookingAsLockedRequest(data ?? null);
  const { plural: coursePluralLabel, singular: courseSingularLabel } =
    useFacilityCourseNoun(data?.startSlot?.agenda?.facility ?? null);

  if (!data) {
    return null;
  }

  const isPrivate =
    data.startSlot?.agenda?.facility?.club?.supportsPrivate ?? false;

  if (data.startSlot?.__typename !== "QueueSlot" && !isLockedRequest) {
    return null;
  }

  const requestedTime = data.request?.time
    ? formatHHMM(data.request.time)
    : undefined;
  const formattedMinTime = data.request?.minTime
    ? formatHHMM(data.request.minTime)
    : undefined;
  const formattedMaxTime = data.request?.maxTime
    ? formatHHMM(data.request.maxTime)
    : undefined;

  const flexibleTime =
    formattedMinTime && formattedMaxTime
      ? `${formattedMinTime} - ${formattedMaxTime}`
      : undefined;

  const groupSize = data?.rounds?.count ?? 1;
  const playerNoun = getBookingPlayerNoun({
    bookingType: data.__typename,
    copyOverrides: data.startSlot?.agenda?.facility?.copyOverrides,
  });

  const holeCount =
    data?.rounds?.edges?.[0]?.node?.courseLayout?.holeCount ?? "EIGHTEEN";
  const holeCountString = getHoleCountString(holeCount);

  const coursePreferences = data?.request?.requestCoursePreferences ?? [];
  const hasCoursePreferences = coursePreferences.length > 0;
  const facilityCourseCount =
    data.startSlot?.agenda?.facility?.courses?.count ?? 0;
  const shouldShowRequestedCourses =
    hasCoursePreferences && facilityCourseCount > 1;

  const coursePreferenceLabels = coursePreferences
    .slice()
    .sort((a, b) => a.index - b.index)
    .map((pref) => {
      if (pref.course) return pref.course.name;
      if (pref.courseBucket) {
        return t`Any ${courseSingularLabel} in ${pref.courseBucket.name}`;
      }
      return t`Any ${courseSingularLabel}`;
    });

  return (
    <Box gap={1} testID="request-details">
      {requestedTime ? (
        <DetailRow
          icon={<ClockIcon />}
          label={t`Time Requested`}
          value={requestedTime}
        />
      ) : null}

      {flexibleTime ? (
        <HStack
          justifyContent="space-between"
          height={48}
          paddingLeft={12}
          paddingTop={2}
        >
          <Text variant="mainBaseMedium">{t`Flexible Window`}</Text>
          <Text variant="mainBaseMedium">{flexibleTime}</Text>
        </HStack>
      ) : null}

      {shouldShowRequestedCourses ? (
        <HStack
          justifyContent="space-between"
          paddingBottom={2}
          paddingTop={2}
          alignItems="flex-start"
        >
          <HStack gap={3} alignItems="center">
            <Box
              width={24}
              height={24}
              backgroundColor="surface-main-primary-subtlest"
              justifyContent="center"
              alignItems="center"
              borderRadius="1"
            >
              <LocationIcon />
            </Box>
            <Text variant="mainBaseSemiBold">
              {t`Requested ${coursePluralLabel}`}
            </Text>
          </HStack>
          <VStack alignItems="flex-end">
            {coursePreferenceLabels.map((label, index) => (
              <Text key={index} variant="mainBaseSemiBold">
                {`${index + 1}. ${label}`}
              </Text>
            ))}
          </VStack>
        </HStack>
      ) : null}

      {!isPrivate ? (
        <>
          <DetailRow
            icon={<PeopleFillIcon />}
            label={t`Group Size`}
            value={
              groupSize === 1
                ? `${groupSize} ${playerNoun.singular}`
                : `${groupSize} ${playerNoun.plural}`
            }
          />

          <DetailRow
            icon={<FlagIcon />}
            label={t`Holes`}
            value={holeCountString}
          />
        </>
      ) : null}
    </Box>
  );
};

export const RequestDetailsFragment = graphql`
  fragment RequestDetails_booking on Booking {
    __typename
    startSlot {
      agenda {
        facility {
          ...useFacilityCourseNoun_facility
          copyOverrides {
            lessonPlayer {
              singular
              plural
            }
            bookingPlayer {
              singular
              plural
            }
          }
          courses {
            count
          }
          club {
            supportsPrivate
          }
        }
      }
    }
    rounds {
      count
      edges {
        node {
          courseLayout {
            holeCount
          }
        }
      }
    }
    ... on StandardBooking {
      request {
        time
        minTime
        maxTime
        requestCoursePreferences {
          index
          course {
            id
            name
          }
          courseBucket {
            id
            name
          }
        }
      }
    }
    ... on LessonBooking {
      request {
        time
        minTime
        maxTime
      }
    }
    ...useShouldPresentBookingAsLockedRequest_booking
    startSlot {
      __typename
    }
  }
`;
